/**
 * Free-of-charge follow-up eligibility (F-SCH-137).
 *
 * Mirrors Flutter `appointment_profile_widget.dart:633-680`: the
 * "Eligible for a free of charge follow-up." banner shows when
 *   1. the selected profile is a follow-up (`appointmentType.code == FOLLOWUP`),
 *   2. a reference date exists (create flow: the previous appointment's date),
 *   3. the slot start is known, and
 *   4. `(start - previousDate).inDays <= billingSettings.followUpValidity`.
 *
 * Dart's `Duration.inDays` truncates toward zero. The previous appointment
 * always precedes the new slot, so the day-gap is non-negative and
 * `Math.floor` matches Dart's truncation exactly.
 */
export interface FreeFollowUpInput {
  /** Selected profile's appointmentType code (already normalised lowercase-keyed). */
  appointmentTypeCode: string | null | undefined;
  /** Previous appointment date (ISO string) — the follow-up reference date. */
  previousAppointmentDate: string | null | undefined;
  /** The picked slot's start (ISO string). */
  slotStart: string | null | undefined;
  /** Org billing setting `followUpValidity` (days); defaults to 0 when absent. */
  followUpValidityDays: number;
}

const FOLLOW_UP_CODE = 'FOLLOWUP';
const MS_PER_DAY = 86_400_000;

export function isFreeFollowUpEligible(input: FreeFollowUpInput): boolean {
  const {
    appointmentTypeCode,
    previousAppointmentDate,
    slotStart,
    followUpValidityDays,
  } = input;

  if (appointmentTypeCode !== FOLLOW_UP_CODE) return false;
  if (!previousAppointmentDate || !slotStart) return false;

  const prevMs = new Date(previousAppointmentDate).getTime();
  const startMs = new Date(slotStart).getTime();
  if (Number.isNaN(prevMs) || Number.isNaN(startMs)) return false;

  // Dart `.inDays` parity — truncate toward zero (gap is non-negative).
  const daysSincePrev = Math.floor((startMs - prevMs) / MS_PER_DAY);
  return daysSincePrev <= followUpValidityDays;
}
